import {
  forwardRef,
  useEffect,
  useId,
  useImperativeHandle,
  useState,
} from "react";
import { Button, Form, Modal } from "react-bootstrap";
import { ErrorMessage, LoadingIndicator } from "src/components/fragments";
import ImageCategorizationFields from "src/components/image/ImageCategorizationFields";
import type { ImageCategorization } from "src/components/image/types";
import {
  ImageTypeScopeEnum,
  useImageCategorization,
  useImageTypeGroups,
  useSetImageOrganized,
  useUpdateImage,
} from "src/graphql";
import { useCurrentUser } from "src/hooks";
import { errorMessage, maxImageDate, partialDateError } from "src/utils";

interface Props {
  imageId: string;
  onPendingChange?: (value: ImageCategorization | null) => void;
}

export interface CategorizationEditor {
  confirmLeave: (proceed: () => void) => void;
  pending: () => ImageCategorization | null;
  reset: () => void;
}

const ImageCategorizationEditor = forwardRef<CategorizationEditor, Props>(
  ({ imageId, onPendingChange }, ref) => {
    const { isEditor, isModerator } = useCurrentUser();
    const controlId = useId();
    const [setOrganized, { loading: organizing }] = useSetImageOrganized();
    const { data, loading, error } = useImageCategorization({ id: imageId });
    const image = data?.findImage;
    const { data: vocabulary } = useImageTypeGroups({
      target: ImageTypeScopeEnum.PERFORMER,
      includeDisabled: true,
    });
    const [draft, setDraft] = useState<ImageCategorization | null>(null);
    const [saveError, setSaveError] = useState<string>();
    const [updateImage, { loading: saving }] = useUpdateImage();

    const [pendingLeave, setPendingLeave] = useState<(() => void) | null>(null);
    const dirty = Boolean(
      image &&
        draft &&
        isEditor &&
        !image.organized &&
        (draft.date !== image.date ||
          draft.types.length !== image.types.length ||
          draft.types.some((type) => !image.types.includes(type))),
    );

    const pending = dirty ? draft : null;

    useImperativeHandle(ref, () => ({
      confirmLeave: (proceed) => {
        if (saving) {
          return;
        }

        if (dirty) {
          setPendingLeave(() => proceed);
        } else {
          proceed();
        }
      },
      pending: () => pending,
      reset: () => setDraft(null),
    }));

    // Lets the surrounding gallery act on labels that have been picked but not
    // saved: a crop label is chosen here and re-cropped to in one sitting
    useEffect(() => {
      onPendingChange?.(pending);
    }, [pending, onPendingChange]);

    useEffect(() => () => onPendingChange?.(null), [onPendingChange]);

    useEffect(() => {
      if (!dirty) {
        return;
      }
      const handler = (event: BeforeUnloadEvent) => {
        event.preventDefault();
        event.returnValue = "";
      };
      window.addEventListener("beforeunload", handler);
      return () => window.removeEventListener("beforeunload", handler);
    }, [dirty]);

    if (loading) {
      return <LoadingIndicator message="Loading categorization..." />;
    }

    if (error) {
      return <ErrorMessage error={errorMessage(error)} />;
    }

    if (!image) {
      return <ErrorMessage error="Image not found" />;
    }

    const value = draft ?? image;
    const dateError = partialDateError(value.date, maxImageDate());

    const commitState = async () => {
      if (!dirty) {
        return true;
      }

      if (dateError) {
        return false;
      }

      setSaveError(undefined);
      try {
        await updateImage({
          variables: {
            imageData: {
              id: imageId,
              types: value.types,
              date: value.date,
            },
          },
        });
        setDraft(null);
        return true;
      } catch (cause) {
        setSaveError(errorMessage(cause));
        return false;
      }
    };

    const toggleOrganized = async () => {
      setSaveError(undefined);

      if (!(await commitState())) {
        return;
      }

      try {
        await setOrganized({
          variables: {
            input: {
              id: imageId,
              organized: !image.organized,
            },
          },
        });
      } catch (cause) {
        setSaveError(errorMessage(cause));
      }
    };

    return (
      <div className="ImageCategorization">
        {isModerator && (
          <Form.Check
            className="ImageCategorization-organized-toggle"
            type="switch"
            id={`${controlId}-organized`}
            label="Organized"
            checked={image.organized}
            disabled={saving || organizing || !!dateError}
            onChange={toggleOrganized}
          />
        )}
        <ImageCategorizationFields
          groups={vocabulary?.imageTypeGroups ?? []}
          value={value}
          onChange={setDraft}
          readOnly={!isEditor || image.organized}
        />
        {saveError && <p className="text-danger">{saveError}</p>}
        {isEditor && !image.organized && (
          <Button
            onClick={commitState}
            disabled={!dirty || saving || !!dateError}
          >
            {saving ? "Saving..." : "Save"}
          </Button>
        )}
        <Modal
          show={!!pendingLeave}
          onHide={() => setPendingLeave(null)}
          centered
        >
          <Modal.Header closeButton>
            <Modal.Title>Discard categorization changes?</Modal.Title>
          </Modal.Header>
          <Modal.Body>Your changes have not been saved.</Modal.Body>
          <Modal.Footer>
            <Button variant="secondary" onClick={() => setPendingLeave(null)}>
              Keep editing
            </Button>
            <Button
              variant="danger"
              onClick={() => {
                const proceed = pendingLeave;
                setPendingLeave(null);
                proceed?.();
              }}
            >
              Discard changes
            </Button>
          </Modal.Footer>
        </Modal>
      </div>
    );
  },
);

export default ImageCategorizationEditor;
